/**
 * 面试列表 —— 4 大模块之一(发起面试 + 看结果)。
 * 含筛选条 + 一键复制候选人链接 + 进报告。
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Avatar } from '../components/Avatar';
import {
  cancelInterview, launchInterviewNow, listCandidates, listInterviews, listJobs,
  stopInterviewNow,
} from '../api/interview';
import type { Candidate, Interview, Job } from '../api/types';

const STATUS_FILTERS: { key: string; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'scheduled', label: '待开始' },
  { key: 'launching', label: '启动中' },
  { key: 'ongoing', label: '面试中' },
  { key: 'completed', label: '已完成' },
];

interface Evaluation {
  overall_score: number | null;
  recommendation: string | null;
}

export default function AdminInterviewList() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [evals, setEvals] = useState<Map<string, Evaluation>>(new Map());
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const statusFilter = params.get('filter') || 'all';

  // silent refresh —— 不切整页 loading,只刷新数据
  const silentRefresh = async () => {
    const [iv, jb, cd] = await Promise.all([listInterviews(), listJobs(), listCandidates()]);
    setInterviews(iv); setJobs(jb); setCandidates(cd);

    const completed = iv.filter((i) => i.status === 'completed');
    const evMap = new Map<string, Evaluation>();
    await Promise.all(completed.map(async (i) => {
      try {
        const r = await fetch(`/api/interviews/${i.id}/full`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}` },
        });
        if (r.ok) {
          const data = await r.json();
          if (data.evaluation) evMap.set(i.id, data.evaluation);
        }
      } catch {}
    }));
    setEvals(evMap);
  };

  // 仅首次挂载用,显示整页"加载中"
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        await silentRefresh();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const jobMap = useMemo(() => new Map(jobs.map((j) => [j.id, j])), [jobs]);
  const candMap = useMemo(() => new Map(candidates.map((c) => [c.id, c])), [candidates]);

  const filtered = useMemo(() => {
    let rows = interviews;
    if (statusFilter !== 'all') rows = rows.filter((i) => i.status === statusFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter((i) => {
        const cand = candMap.get(i.candidate_id);
        const job = jobMap.get(i.job_id);
        return ((cand?.name || '').toLowerCase().includes(q)
          || (cand?.email || '').toLowerCase().includes(q)
          || (job?.title || '').toLowerCase().includes(q));
      });
    }
    return rows;
  }, [interviews, statusFilter, search, jobMap, candMap]);

  const counts: Record<string, number> = { all: interviews.length };
  for (const i of interviews) counts[i.status] = (counts[i.status] || 0) + 1;

  const handleCopyLink = async (token: string) => {
    const url = `${window.location.origin}/interview/${token}`;
    await navigator.clipboard.writeText(url);
    alert('候选人链接已复制\n' + url);
  };
  const handleLaunch = async (id: string) => {
    try {
      const iv = await launchInterviewNow(id);
      alert(`✅ 已发送 AI 入会指令(状态:${iv.status})。AI 会在 30-60s 内进入 RTC 房间。\n如候选人侧仍'等待入会',稍后用'诊断 AI 状态'查看详情。`);
      await silentRefresh();
    } catch (e: any) { alert('拉起失败:' + e.message); }
  };
  const handleDiagnose = async (id: string) => {
    try {
      const { api } = await import('../api/client');
      const r = await api.get(`/api/interviews/${id}/agent-status`);
      const d = r.data;
      const credsOk = (d.missing_credentials?.length ?? 0) === 0;
      alert([
        `面试状态:${d.interview_status}`,
        `RTC 任务 ID:${d.rtc_task_id || '(未拉起)'}`,
        `凭据完整性:${credsOk ? '✅ 齐全' : `❌ 缺 ${d.missing_credentials.join(', ')}`}`,
        `字幕回调:${d.callback_url_configured ? '✅ 已配' : '⚠️ 未配(本地无 transcript 正常)'}`,
        `Transcript:${d.transcript_total_turns} 句(AI ${d.transcript_ai_turns} 句)`,
        '',
        '【诊断结论】',
        d.diagnostic,
      ].join('\n'));
    } catch (e: any) { alert('诊断失败:' + e.message); }
  };
  const handleStop = async (id: string) => {
    if (!confirm('停止 AI 并标记面试完成?')) return;
    try { await stopInterviewNow(id); await silentRefresh(); }
    catch (e: any) { alert(e.message); }
  };
  const handleCancel = async (id: string) => {
    if (!confirm('取消这场面试?候选人将无法再加入。')) return;
    try { await cancelInterview(id); await silentRefresh(); }
    catch (e: any) { alert(e.message); }
  };

  return (
    <div className="container">
      <div className="page-header">
        <div>
          <h1 className="title">面试</h1>
          <div className="subtitle">{filtered.length} 条 / 共 {interviews.length}</div>
        </div>
        <div className="actions">
          <Link to="/admin/new"><button>+ 新建面试</button></Link>
        </div>
      </div>

      <div className="filter-bar">
        {STATUS_FILTERS.map((f) => (
          <span key={f.key}
                className={`chip ${statusFilter === f.key ? 'active' : ''}`}
                onClick={() => setParams(f.key === 'all' ? {} : { filter: f.key })}>
            {f.label}{counts[f.key] != null ? ` ${counts[f.key]}` : ''}
          </span>
        ))}
        <div style={{ flex: 1 }} />
        <input
          placeholder="搜索候选人 / 邮箱 / 岗位..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 320 }}
        />
      </div>

      {loading ? (
        <div className="hint">加载中...</div>
      ) : filtered.length === 0 ? (
        <div className="card">
          <div className="empty">
            <div className="icon">📭</div>
            <div>{search || statusFilter !== 'all' ? '没有匹配的面试' : '还没有面试'}</div>
            {!search && statusFilter === 'all' && (
              <div style={{ marginTop: 16 }}>
                <Link to="/admin/new"><button>新建第一场面试</button></Link>
              </div>
            )}
          </div>
        </div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>候选人 / 岗位</th>
              <th>面试官</th>
              <th>预约时间</th>
              <th>状态</th>
              <th>评分</th>
              <th>推荐</th>
              <th style={{ width: 220 }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((iv) => {
              const cand = candMap.get(iv.candidate_id);
              const job = jobMap.get(iv.job_id);
              const ev = evals.get(iv.id);
              const t = new Date(iv.scheduled_at);
              return (
                <tr key={iv.id} style={{ cursor: 'pointer' }}
                    onClick={() => nav(`/admin/interview/${iv.id}`)}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{cand?.name || '(未命名)'}</div>
                    <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{job?.title || '?'}</div>
                  </td>
                  <td>
                    <span className="row" style={{ gap: 6 }}>
                      <Avatar value={job?.interviewer_avatar} name={job?.interviewer_name} size={24} />
                      <span style={{ fontSize: 13 }}>{job?.interviewer_name || '李先生'}</span>
                    </span>
                  </td>
                  <td>
                    <div>{t.toLocaleString('zh-CN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                    <div className="muted" style={{ fontSize: 12 }}>{iv.duration_minutes} 分钟</div>
                  </td>
                  <td><span className={`tag ${iv.status}`}>{statusLabel(iv.status)}</span></td>
                  <td>
                    <span className={`score ${scoreClass(ev?.overall_score)}`}>
                      <span className="num">{ev?.overall_score?.toFixed(1) ?? '—'}</span>
                    </span>
                  </td>
                  <td><span className={`rec ${ev?.recommendation || 'pending'}`}>{recLabel(ev?.recommendation)}</span></td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                      <button className="secondary icon" onClick={() => nav(`/admin/interview/${iv.id}`)}>📊 报告</button>
                      <button className="ghost icon" onClick={() => handleCopyLink(iv.access_token)}>🔗</button>
                      {iv.status === 'scheduled' && <button className="icon" onClick={() => handleLaunch(iv.id)} title="让 AI 立即进 RTC 房间">▶ 拉起 AI</button>}
                      {['launching', 'ongoing'].includes(iv.status) && (
                        <>
                          <button className="ghost icon" onClick={() => handleDiagnose(iv.id)} title="查 AI 是否真的进了房间">🔍 诊断</button>
                          <button className="secondary icon" onClick={() => handleStop(iv.id)} title="停止 AI">⏹</button>
                        </>
                      )}
                      {!['completed', 'cancelled', 'failed'].includes(iv.status) &&
                        <button className="ghost icon" onClick={() => handleCancel(iv.id)}>×</button>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function scoreClass(s: number | null | undefined): string {
  if (s == null) return 'none';
  if (s >= 8) return 'high';
  if (s >= 6) return 'mid';
  return 'low';
}
function statusLabel(s: string): string {
  return ({
    scheduled: '待开始', launching: '启动中', ongoing: '面试中',
    completed: '已完成', failed: '失败', cancelled: '已取消',
  } as Record<string, string>)[s] || s;
}
function recLabel(r: string | null | undefined): string {
  if (!r) return '待评估';
  return ({
    strong_yes: '强烈推荐', yes: '推荐', maybe: '可考虑', no: '不推荐',
  } as Record<string, string>)[r] || r;
}
